import { useState } from 'react';
import { Helmet } from 'react-helmet';
import { Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

const FAQS = [
  {
    category: 'Sobre el servicio',
    color: '#005F6A',
    items: [
      {
        q: '¿Qué es Buscakine?',
        a: 'Buscakine.cl es un directorio online que conecta a pacientes con kinesiólogos certificados en todo Chile, para atención a domicilio o en consulta. No somos un centro médico ni prestamos servicios clínicos: facilitamos el contacto entre pacientes y profesionales independientes.'
      },
      {
        q: '¿Cómo encuentro un kinesiólogo cerca de mí?',
        a: 'Ingresa al buscador de kinesiólogos y filtra por región, comuna y especialidad. Cada perfil muestra las comunas donde el profesional atiende, su formación y sus calificaciones.'
      },
      {
        q: '¿Puedo agendar atención de kinesiología a domicilio?',
        a: 'Sí. Muchos kinesiólogos de la plataforma ofrecen atención a domicilio además de consulta presencial. Puedes filtrar por esta modalidad directamente en el buscador.'
      },
      {
        q: '¿Qué especialidades de kinesiología puedo encontrar?',
        a: 'Musculoesquelético/Traumatología, Neurorehabilitación, Kinesiología Respiratoria, Suelo Pélvico, Kinesiología Geriátrica, Kinesiología Dermatofuncional y Quiropráctico, entre otras.'
      },
      {
        q: '¿Los kinesiólogos están certificados?',
        a: 'Cada kinesiólogo debe declarar su título profesional y su número de Registro en la Superintendencia de Salud, visible en su perfil. Además pueden subir certificados de diplomados, cursos o magísteres.'
      }
    ]
  },
  {
    category: 'Pagos y convenios',
    color: '#2e7d32',
    items: [
      {
        q: '¿Buscakine tiene convenio con Fonasa o Isapres?',
        a: 'Cada kinesiólogo indica en su perfil las modalidades de pago que acepta: Fonasa, particular o reembolso por Isapre. Te recomendamos confirmar directamente con el profesional antes de agendar.'
      },
      {
        q: '¿Cuánto cuesta una sesión de kinesiología?',
        a: 'El valor lo fija cada kinesiólogo de forma independiente y se muestra en su perfil, según la especialidad o prestación. Buscakine no cobra a los pacientes por usar la plataforma ni por contactar profesionales.'
      }
    ]
  },
  {
    category: 'Contacto y reseñas',
    color: '#1a6bc5',
    items: [
      {
        q: '¿Cómo contacto a un kinesiólogo?',
        a: 'Desde el perfil del profesional puedes contactarlo directamente por WhatsApp. Solo necesitas crear una cuenta gratuita de paciente para ver sus datos de contacto.'
      },
      {
        q: '¿Puedo ver reseñas y calificaciones de los kinesiólogos?',
        a: 'Sí, cada perfil muestra la calificación promedio del profesional en base a evaluaciones de pacientes reales que ya recibieron atención.'
      },
      {
        q: '¿Cómo me registro como paciente?',
        a: 'Haz clic en "Iniciar sesión" y elige la opción de registro, seleccionando el tipo de usuario "Paciente". El registro es gratuito.'
      },
      {
        q: '¿Buscakine realiza los tratamientos o diagnósticos?',
        a: 'No. Buscakine.cl es exclusivamente una plataforma de intermediación digital. La atención clínica, el diagnóstico y el tratamiento son responsabilidad exclusiva del kinesiólogo tratante.'
      }
    ]
  },
  {
    category: 'Para kinesiólogos — Registrarse en el directorio',
    color: '#6a1b9a',
    items: [
      {
        q: '¿Cómo me registro como kinesiólogo en la plataforma?',
        a: 'Regístrate seleccionando el tipo de usuario "Kinesiólogo", completa tu perfil profesional (título, especialidades, comunas de atención, registro en la Superintendencia de Salud) y sube tus credenciales para aumentar la confianza de los pacientes.'
      },
      {
        q: '¿Por qué se cobra una comisión por derivación?',
        a: 'Buscakine.cl cobra una comisión de $10.000 por cada derivación de contacto, que habilita tu número de WhatsApp visible para todos los pacientes que visiten tu perfil. Es un pago único por derivación, no una suscripción mensual.'
      },
      {
        q: '¿Cómo funcionan las reseñas de pacientes?',
        a: 'Los pacientes registrados pueden evaluar tu trabajo dejando una calificación de 1 a 5 estrellas y un comentario. Las reseñas aprobadas aparecen en tu perfil público y contribuyen a tu calificación promedio.'
      }
    ]
  }
];

const faqJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: FAQS.flatMap(cat =>
    cat.items.map(item => ({
      '@type': 'Question',
      name: item.q,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.a
      }
    }))
  )
};

export default function FAQ(props: any) {
  const { setLoading } = props;
  const [expanded, setExpanded] = useState<string | false>(false);

  const handleChange = (panel: string) => (_: any, isExpanded: boolean) => {
    setExpanded(isExpanded ? panel : false);
  };

  if (setLoading) setLoading(false);

  return (
    <>
      <Helmet>
        <title>Preguntas Frecuentes — Kinesiología a Domicilio | Buscakine</title>
        <meta name="description" content="Resuelve tus dudas sobre cómo encontrar un kinesiólogo, agendar atención a domicilio o en consulta, y cómo funciona Buscakine.cl." />
        <link rel="canonical" href="https://buscakine.cl/preguntas-frecuentes" />
        <script type="application/ld+json">{JSON.stringify(faqJsonLd)}</script>
      </Helmet>

      {/* Hero */}
      <div style={{
        background: 'linear-gradient(135deg, #005F6A 0%, #1a6bc5 100%)',
        padding: '56px 24px',
        textAlign: 'center',
        color: '#fff'
      }}>
        <h1 style={{ fontSize: '32px', fontWeight: 700, margin: '0 0 12px', lineHeight: 1.2 }}>
          Preguntas Frecuentes
        </h1>
        <p style={{ fontSize: '17px', fontWeight: 300, margin: 0, opacity: 0.9, maxWidth: '600px', marginLeft: 'auto', marginRight: 'auto' }}>
          Todo lo que necesitas saber sobre nuestro servicio y cómo encontrar al kinesiólogo adecuado.
        </p>
      </div>

      <div style={{ maxWidth: '860px', margin: '0 auto', padding: '40px 20px 60px' }}>

        {FAQS.map((section, si) => (
          <div key={si} style={{ marginBottom: '40px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
              <div style={{
                width: '4px', height: '28px', borderRadius: '2px',
                background: section.color, flexShrink: 0
              }} />
              <h2 style={{ margin: 0, fontSize: '20px', fontWeight: 700, color: '#111827' }}>
                {section.category}
              </h2>
            </div>

            {section.items.map((item, ii) => {
              const key = `${si}-${ii}`;
              return (
                <Accordion
                  key={key}
                  expanded={expanded === key}
                  onChange={handleChange(key)}
                  elevation={0}
                  sx={{
                    border: '1px solid #e5e7eb',
                    borderRadius: '10px !important',
                    mb: 1.5,
                    '&:before': { display: 'none' },
                    '&.Mui-expanded': {
                      borderColor: section.color,
                      boxShadow: `0 4px 20px ${section.color}22`
                    }
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    sx={{
                      fontWeight: 600,
                      fontSize: '15px',
                      color: expanded === key ? section.color : '#111827',
                      '& .MuiAccordionSummary-expandIconWrapper': {
                        color: expanded === key ? section.color : '#6b7280'
                      }
                    }}
                  >
                    {item.q}
                  </AccordionSummary>
                  <AccordionDetails sx={{ color: '#444', fontSize: '14px', lineHeight: 1.8, pt: 0 }}>
                    {item.a}
                  </AccordionDetails>
                </Accordion>
              );
            })}
          </div>
        ))}

        {/* CTA final */}
        <div style={{
          background: 'linear-gradient(135deg, #005F6A 0%, #1a6bc5 100%)',
          borderRadius: '16px',
          padding: '36px 32px',
          textAlign: 'center',
          color: '#fff',
          marginTop: '40px'
        }}>
          <h2 style={{ margin: '0 0 10px', fontSize: '22px', fontWeight: 700, color: '#fff' }}>
            ¿No encontraste tu respuesta?
          </h2>
          <p style={{ margin: '0 0 20px', opacity: 0.88, fontSize: '15px' }}>
            Busca un kinesiólogo en tu comuna y consúltale directamente.
          </p>
          <a
            href="/kinesiologos"
            style={{
              display: 'inline-block',
              background: '#fff',
              color: '#005F6A',
              fontWeight: 700,
              padding: '12px 32px',
              borderRadius: '8px',
              textDecoration: 'none',
              fontSize: '15px'
            }}
          >
            Buscar kinesiólogo →
          </a>
        </div>
      </div>
    </>
  );
}
